import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "./../../components/card/card.jsx";
import Chart from "react-apexcharts";
import Masonry from "masonry-layout";
import { ModalComponent } from "../expand/expandmodal.jsx";

const Webflowrealtime = () => {
  // =========================================================
  // 1) HARD SIZE LOCKS (this is what fixes the “middle shorter”)
  // =========================================================
  const TOP_HEADER_HEIGHT = 140;        // reserve equal header height for ALL top cards
  const CHART_SLOT_HEIGHT = 260;        // chart zone height (fixed)
  const TOP_FOOTER_HEIGHT = 44;         // reserved footer (refresh note area)
  const TOP_CHART_AREA_HEIGHT = CHART_SLOT_HEIGHT - TOP_FOOTER_HEIGHT;

  // Device card needs a footer area for metrics; keep it inside the same slot
  const DEVICE_FOOTER_HEIGHT = 56;
  const DEVICE_CHART_AREA_HEIGHT = CHART_SLOT_HEIGHT - DEVICE_FOOTER_HEIGHT;

  // =========================================================
  // THEME VARS
  // =========================================================
  const themeFont = getComputedStyle(document.body)
    .getPropertyValue("--bs-body-font-family")
    .trim();

  const themeFontWeight = getComputedStyle(document.body)
    .getPropertyValue("--bs-body-font-weight")
    .trim();

  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  const inverse = getComputedStyle(document.body)
    .getPropertyValue("--bs-inverse")
    .trim();

  const inverseRgb = getComputedStyle(document.body)
    .getPropertyValue("--bs-inverse-rgb")
    .trim();

  // =========================================================
  // UI HELPERS
  // =========================================================
  const HeaderBlock = ({ children }) => (
    <div
      style={{
        minHeight: TOP_HEADER_HEIGHT,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      {children}
    </div>
  );

  const ChartSlot = ({ children }) => (
    <div style={{ height: CHART_SLOT_HEIGHT, width: "100%" }}>{children}</div>
  );

  const InlineSpinner = ({ size = 26 }) => (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: size,
          height: size,
          border: `3px solid rgba(${inverseRgb},0.15)`,
          borderTop: `3px solid ${themeColor}`,
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  const NoData = ({ text }) => (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: inverse,
        opacity: 0.6,
        fontSize: 13,
        padding: 12,
      }}
    >
      {text}
    </div>
  );

  const RefreshNote = () => (
    <div style={{ color: themeColor, fontSize: 12, textAlign: "center" }}>
      Refreshing…
    </div>
  );

  // =========================================================
  // DONUT OPTIONS (no internal labels, no legend)
  // =========================================================
  const getDonutOptions = () => {
    const themeColorRgb = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme-rgb")
      .trim();

    return {
      colors: [
        `rgba(${themeColorRgb}, .95)`,
        `rgba(${themeColorRgb}, .55)`,
        `rgba(${themeColorRgb}, .35)`,
        `rgba(${themeColorRgb}, .75)`,
      ],
      chart: {
        type: "donut",
        sparkline: { enabled: true },
        foreColor: inverse,
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { show: false },
      tooltip: {
        theme: "dark",
        x: { show: false },
        y: { title: { formatter: () => "" } },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "72%",
            labels: { show: false },
          },
        },
      },
      labels: [],
    };
  };

  // =========================================================
  // STATE
  // =========================================================
  const [donutOptions, setDonutOptions] = useState(getDonutOptions());
  const [sourceChartData, setSourceChartData] = useState([]);
  const [RealTimeDeviceTile, setDataRealtimeDeviceTile] = useState(0);
  const [dimensionMetrics, setDimensionMetrics] = useState([]);

  const [Nubaresponse, setNubaresponse] = useState([]);
  const [Vbptresponse, setVbptresponse] = useState([]);
  const [Ecenresponse, setEcenresponse] = useState([]);

  // loading flags
  const [loadingDevice, setLoadingDevice] = useState(true);
  const [loadingNuba, setLoadingNuba] = useState(true);
  const [loadingVbpt, setLoadingVbpt] = useState(true);
  const [loadingEcen, setLoadingEcen] = useState(true);

  // refreshing flags
  const [refreshingDevice, setRefreshingDevice] = useState(false);
  const [refreshingNuba, setRefreshingNuba] = useState(false);
  const [refreshingVbpt, setRefreshingVbpt] = useState(false);
  const [refreshingEcen, setRefreshingEcen] = useState(false);

  // =========================================================
  // DERIVED
  // =========================================================
  const Nubavalue =
    Nubaresponse.length > 0 ? Nubaresponse[0].metricValues?.[0]?.value : null;
  const Nubaheading =
    Nubaresponse.length > 0 ? Nubaresponse[0].dimensionValues?.[0]?.value : null;

  // --- VBPT: compute true #1 by max metric, label blank => "Unknown"
  const vbptTop = useMemo(() => {
    const rows = Vbptresponse ?? [];
    if (!rows.length) return { heading: null, value: null };

    let bestValue = -Infinity;
    let bestHeading = null;

    for (const r of rows) {
      const v = Number(r?.metricValues?.[0]?.value ?? 0);
      if (v > bestValue) {
        bestValue = v;
        const raw = (r?.dimensionValues?.[0]?.value ?? '').trim();
        bestHeading = raw || 'Unknown';
      }
    }

    return { heading: bestHeading, value: bestValue };
  }, [Vbptresponse]);

  const Vbptheading = vbptTop.heading;
  const Vbptvalue = vbptTop.value;


  const Ecenvalue =
    Ecenresponse.length > 0 ? Ecenresponse[0].metricValues?.[0]?.value : null;
  const Ecenheading =
    Ecenresponse.length > 0 ? Ecenresponse[0].dimensionValues?.[0]?.value : null;

  const nubalabel = (Nubaresponse || []).map(
    (item) => item.dimensionValues?.[0]?.value || "Unknown"
  );
  const nubavalues = (Nubaresponse || []).map(
    (item) => parseInt(item.metricValues?.[0]?.value || "0", 10) || 0
  );

  const vbptlabel = (Vbptresponse || [])
    .map((item) => item.dimensionValues?.[0]?.value || "Unknown")
    .slice(0, 10);
  const vbptvalues = (Vbptresponse || [])
    .map((item) => parseInt(item.metricValues?.[0]?.value || "0", 10) || 0)
    .slice(0, 10);

  const ecenlabel = (Ecenresponse || []).map(
    (item) => item.dimensionValues?.[0]?.value || "Unknown"
  );
  const ecenvalues = (Ecenresponse || []).map(
    (item) => parseInt(item.metricValues?.[0]?.value || "0", 10) || 0
  );

  // =========================================================
  // BAR OPTIONS (shared baseline)
  // =========================================================
  const commonBar = useMemo(
    () => ({
      chart: { toolbar: { show: true }, foreColor: inverse, fontFamily: themeFont },
      tooltip: { theme: "dark" },
      grid: { borderColor: `rgba(${inverseRgb}, 0.15)` },
      colors: [themeColor],
      stroke: { show: true },
      legend: { labels: { colors: inverse }, fontFamily: themeFont },
    }),
    [inverse, inverseRgb, themeColor, themeFont]
  );

  const NubaChartOptions = useMemo(
    () => ({
      ...commonBar,
      plotOptions: { bar: { horizontal: false, dataLabels: { position: "top" } } },
      dataLabels: {
        enabled: true,
        offsetY: -18,
        style: { fontSize: "12px", colors: [inverse] },
      },
      xaxis: {
        categories: nubalabel,
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
      yaxis: {
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
    }),
    [commonBar, nubalabel, inverse, themeFont, themeFontWeight]
  );

  const VbptChartOptions = useMemo(
    () => ({
      ...commonBar,
      plotOptions: { bar: { horizontal: true, dataLabels: { position: "top" } } },
      dataLabels: {
        enabled: true,
        offsetX: 28,
        style: { fontSize: "12px", colors: [inverse] },
      },
      xaxis: {
        categories: vbptlabel,
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
      yaxis: {
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
    }),
    [commonBar, vbptlabel, inverse, themeFont, themeFontWeight]
  );

  const EcenChartOptions = useMemo(
    () => ({
      ...commonBar,
      plotOptions: { bar: { horizontal: true, dataLabels: { position: "top" } } },
      dataLabels: {
        enabled: true,
        offsetX: 28,
        style: { fontSize: "12px", colors: [inverse] },
      },
      xaxis: {
        categories: ecenlabel,
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
      yaxis: {
        labels: {
          style: { colors: inverse, fontSize: "12px", fontFamily: themeFont, fontWeight: themeFontWeight },
        },
      },
    }),
    [commonBar, ecenlabel, inverse, themeFont, themeFontWeight]
  );

  const NubaChartData = useMemo(() => [{ name: "Value", data: nubavalues }], [nubavalues]);
  const VbptChartData = useMemo(() => [{ name: "Value", data: vbptvalues }], [vbptvalues]);
  const EcenChartData = useMemo(() => [{ name: "Value", data: ecenvalues }], [ecenvalues]);

  // =========================================================
  // MODAL EXPAND (unchanged)
  // =========================================================
  const VbptExpand = () => (
    <>
      <h3 className="mb-2">Views by Page Title and Screen Name</h3>
      <div>
        <Chart type="bar" options={{ ...VbptChartOptions, xaxis: { ...VbptChartOptions.xaxis, categories: vbptlabel } }} series={VbptChartData} height={2500} />
      </div>
    </>
  );

  // =========================================================
  // MASONRY
  // =========================================================
  const [masonryInstance, setMasonryInstance] = useState(null);

  useEffect(() => {
    const grid = document.querySelector("[data-masonry]");
    if (!grid) return;

    const msnry = new Masonry(grid, { percentPosition: true });
    setMasonryInstance(msnry);

    return () => {
      try { msnry.destroy(); } catch (_) {}
    };
  }, []);

  const relayoutMasonry = () => {
    // layout after React paint + after Apex renders
    requestAnimationFrame(() => {
      masonryInstance?.layout();
      setTimeout(() => masonryInstance?.layout(), 250);
      setTimeout(() => masonryInstance?.layout(), 900);
    });
  };

  // =========================================================
  // FETCH + AUTO-REFRESH
  // =========================================================
  useEffect(() => {
    let mounted = true;
    let inFlight = false;

    const RealtimeDataInfo = async (isRefresh = false) => {
      if (inFlight) return;
      inFlight = true;

      try {
        if (!isRefresh) {
          setLoadingDevice(true);
          setLoadingNuba(true);
          setLoadingVbpt(true);
          setLoadingEcen(true);
        } else {
          setRefreshingDevice(true);
          setRefreshingNuba(true);
          setRefreshingVbpt(true);
          setRefreshingEcen(true);
        }

        const token = localStorage.getItem("jwtToken");
        const response = await fetch(
          `${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/webflow/ga4/realtimeanalytics/realdata`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        if (!mounted) return;

        const deviceRows = result?.deviceresponse || [];
        const nubaRows = result?.nubaresponse || [];
        const vbptRows = result?.vbptresponse || [];
        const ecenRows = result?.ecenresponse || [];

        setDataRealtimeDeviceTile(result?.totalCountryActiveUser ?? 0);
        setNubaresponse(nubaRows);
        setVbptresponse(vbptRows);
        setEcenresponse(ecenRows);

        const chartData = deviceRows.map((item) =>
          parseInt(item?.metricValues?.[0]?.value || "0", 10)
        );
        const labels = deviceRows.map(
          (item) => item?.dimensionValues?.[0]?.value || "Unknown"
        );

        setSourceChartData(chartData);

        setDimensionMetrics(
          deviceRows.map((item) => ({
            dimension: item?.dimensionValues?.[0]?.value || "Unknown",
            metric: item?.metricValues?.[0]?.value || "0",
          }))
        );

        setDonutOptions((prev) => ({ ...prev, labels }));

        relayoutMasonry();
      } catch (e) {
        if (!mounted) return;
        console.error("Realtime fetch error:", e);
      } finally {
        if (!mounted) return;

        setLoadingDevice(false);
        setLoadingNuba(false);
        setLoadingVbpt(false);
        setLoadingEcen(false);

        setRefreshingDevice(false);
        setRefreshingNuba(false);
        setRefreshingVbpt(false);
        setRefreshingEcen(false);

        inFlight = false;
      }
    };

    RealtimeDataInfo(false);
    const id = setInterval(() => RealtimeDataInfo(true), 60000);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [masonryInstance]);

  // theme reload
  useEffect(() => {
    const handler = () => setDonutOptions(getDonutOptions());
    document.addEventListener("theme-reload", handler);
    return () => document.removeEventListener("theme-reload", handler);
  }, []);

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-6 col-lg-6">
          <h6>
            <span style={{ color: themeColor }}>PAGES</span> / SHAKER PRO GOOGLE ANALYTICS
          </h6>
          <h2>REAL-TIME DATA</h2>
        </div>
      </div>

      <div className="row" data-masonry='{"percentPosition": true }'>
        {/* ===================== DEVICE (TOP LEFT) ===================== */}
        <div className="col-xl-4 col-lg-4 col-md-12 mb-3">
          <Card className="mb-3">
            <CardBody>
              <HeaderBlock>
                <h6 className="mb-3">Users in 30 minutes</h6>
                <div className="d-flex fw-bold small mb-2">
                  <h2>{RealTimeDeviceTile}</h2>
                </div>
                <h5 className="mb-3">DEVICE CATEGORY IN LAST 30 MINUTES</h5>
              </HeaderBlock>

              <ChartSlot>
                {/* Chart area */}
                <div style={{ height: DEVICE_CHART_AREA_HEIGHT, width: "100%" }}>
                  {loadingDevice ? (
                    <InlineSpinner />
                  ) : sourceChartData.length > 0 ? (
                    <Chart
                      type="donut"
                      height={DEVICE_CHART_AREA_HEIGHT}
                      options={donutOptions}
                      series={sourceChartData}
                    />
                  ) : (
                    <NoData text="No device data yet" />
                  )}
                </div>

                {/* Footer reserved */}
                <div
                  style={{
                    height: DEVICE_FOOTER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {refreshingDevice ? (
                    <RefreshNote />
                  ) : !loadingDevice && dimensionMetrics.length > 0 ? (
                    <div
                      className="d-flex justify-content-around align-items-center"
                      style={{ width: "100%" }}
                    >
                      {dimensionMetrics.map((item, index) => (
                        <div key={index} className="text-center" style={{ lineHeight: 1.1, color: inverse }}>
                          {String(item.dimension).toUpperCase()}
                          <br />
                          {item.metric}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              </ChartSlot>
            </CardBody>
          </Card>
        </div>

        {/* ===================== AUDIENCE (TOP MIDDLE) ===================== */}
        <div className="col-xl-4 col-lg-4 col-md-12 mb-3">
          <Card className="mb-3">
            <CardBody>
              <HeaderBlock>
                <h6 className="mb-2">Users by Audience</h6>
                <h5 className="mt-2">#1 {Nubaheading || "—"}</h5>
                <h2 className="mt-2">{Nubavalue ?? 0}</h2>
              </HeaderBlock>

              <ChartSlot>
                {/* Chart area */}
                <div style={{ height: TOP_CHART_AREA_HEIGHT, width: "100%" }}>
                  {loadingNuba ? (
                    <InlineSpinner />
                  ) : nubavalues.length > 0 ? (
                    <Chart
                      type="bar"
                      options={NubaChartOptions}
                      series={NubaChartData}
                      height={TOP_CHART_AREA_HEIGHT}
                    />
                  ) : (
                    <NoData text="No audience data yet" />
                  )}
                </div>

                {/* Footer reserved (THIS is the ONLY refresh text place) */}
                <div
                  style={{
                    height: TOP_FOOTER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {refreshingNuba ? <RefreshNote /> : <div />}
                </div>
              </ChartSlot>
            </CardBody>
          </Card>
        </div>

        {/* ===================== VBPT (TOP RIGHT) ===================== */}
        <div className="col-xl-4 col-lg-4 col-md-12 mb-3">
          <Card className="mb-3">
            <CardBody>
              <HeaderBlock>
                <div className="d-flex fw-bold small">
                  <div className="flex-grow-1 me-2">
                    <h6 className="col-md-12">Views by Page Title and Screen Name</h6>
                  </div>
                  <ModalComponent cardBodyContent={<VbptExpand />} />
                </div>

                <h5 className="mt-2">#1 {Vbptheading || "—"}</h5>
                <h2 className="mt-2">{Vbptvalue ?? 0}</h2>
              </HeaderBlock>

              <ChartSlot>
                <div style={{ height: TOP_CHART_AREA_HEIGHT, width: "100%" }}>
                  {loadingVbpt ? (
                    <InlineSpinner />
                  ) : vbptvalues.length > 0 ? (
                    <Chart
                      type="bar"
                      options={VbptChartOptions}
                      series={VbptChartData}
                      height={TOP_CHART_AREA_HEIGHT}
                    />
                  ) : (
                    <NoData text="No page view data yet" />
                  )}
                </div>

                <div
                  style={{
                    height: TOP_FOOTER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {refreshingVbpt ? <RefreshNote /> : <div />}
                </div>
              </ChartSlot>
            </CardBody>
          </Card>
        </div>

        {/* ===================== ECEN (BOTTOM) ===================== */}
        <div className="col-xl-4 col-lg-4 col-md-12 mb-3">
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-2">Event Count by Event Name</h6>
              <h5 className="mt-2">#1 {Ecenheading || "—"}</h5>
              <h2 className="mt-2">{Ecenvalue ?? 0}</h2>

              <div style={{ height: CHART_SLOT_HEIGHT, width: "100%" }}>
                {loadingEcen ? (
                  <InlineSpinner />
                ) : ecenvalues.length > 0 ? (
                  <Chart type="bar" options={EcenChartOptions} series={EcenChartData} height={CHART_SLOT_HEIGHT} />
                ) : (
                  <NoData text="No event data yet" />
                )}
              </div>

              <div style={{ height: TOP_FOOTER_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {refreshingEcen ? <RefreshNote /> : <div />}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Webflowrealtime;
